import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check, Loader2, MessageSquare, RefreshCw } from 'lucide-react';
import {
  OPEN_ROUTING_FEEDBACK_STATUSES,
  ROUTING_FEEDBACK_KINDS,
  ROUTING_FEEDBACK_STATUSES,
  routingFeedbackKind,
  routingFeedbackStatus,
  type RoutingFeedbackKind,
  type RoutingFeedbackStatus,
} from '@repo/shared-types';
import { AdminSidebar } from '../../components/layout/AdminSidebar';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api, type RoutingFeedbackQueue, type RoutingFeedbackWithRun } from '../../lib/api';
import { formatRunLabel, formatSource, useSourceLabels } from '../../lib/source-label';
import { cn, formatDate, timeAgo } from '../../lib/utils';

/**
 * The triage queue for what people said the ingestion agent got wrong.
 *
 * An administrative view even though leaving feedback is not: it spans every reporter and every
 * project, and exists to decide what to act on. Each kind points at a different fix — a wrong
 * destination is usually a project description, a bad split is the segmenter's prompt, "should not
 * have been absorbed" is a source that shouldn't be watched — which is why the queue groups by kind
 * rather than scoring decisions.
 *
 * Nothing here deletes. An administrator who disagrees **dismisses**, which keeps the report and
 * records why; a queue holding only the complaints administrators accepted could not be used to judge
 * the router at all.
 */
export default function AdminFeedback() {
  const [queue, setQueue] = useState<RoutingFeedbackQueue | null>(null);
  const [statuses, setStatuses] = useState<RoutingFeedbackStatus[]>(OPEN_ROUTING_FEEDBACK_STATUSES);
  const [kinds, setKinds] = useState<RoutingFeedbackKind[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.admin
      .listRoutingFeedback({ statuses, kinds })
      .then((page) => {
        setQueue(page);
        setError(null);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [statuses, kinds]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = (status: RoutingFeedbackStatus) =>
    setStatuses((prev) => (prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]));

  const toggleKind = (kind: RoutingFeedbackKind) =>
    setKinds((prev) => (prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind]));

  const stats = queue?.stats;
  const openCount = useMemo(
    () => OPEN_ROUTING_FEEDBACK_STATUSES.reduce((sum, status) => sum + (stats?.byStatus[status] ?? 0), 0),
    [stats],
  );

  return (
    <div className="flex h-screen overflow-hidden bg-surface">
      <AdminSidebar />

      <div className="pl-56 flex-1 h-full overflow-y-auto">
        <div className="max-w-4xl mx-auto px-7 py-7">
          <div className="flex items-start gap-3 mb-5">
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-ink">Ingest feedback</h1>
              <p className="text-xs text-ink-muted mt-1 max-w-2xl leading-relaxed">
                What people said the ingestion agent got wrong. Each kind points somewhere different:
                a wrong destination is usually a project description, a duplicated page is the
                search-before-create rule, and "should not have been absorbed" is a source that should
                not be watched.
              </p>
            </div>
            <button
              onClick={load}
              disabled={loading}
              className="ml-auto text-ink-faint hover:text-ink transition-colors disabled:opacity-40"
              title="Refresh"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : undefined} />
            </button>
          </div>

          {/* Totals over everything, not over the filtered page — otherwise narrowing the list would
              silently move the numbers it is being judged against. */}
          {stats && stats.total > 0 && (
            <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 pb-3 mb-4 border-b border-border-subtle">
              <span className="text-[11px] text-ink-faint">
                <span className="text-sm font-medium tabular-nums text-ink-secondary">{stats.total}</span> reported
              </span>
              <span className="text-[11px] text-ink-faint">
                <span
                  className={cn(
                    'text-sm font-medium tabular-nums',
                    openCount > 0 ? 'text-warning-text' : 'text-ink-faint',
                  )}
                >
                  {openCount}
                </span>{' '}
                needing you
              </span>
              {ROUTING_FEEDBACK_KINDS.filter((kind) => (stats.byKind[kind.kind] ?? 0) > 0).map((kind) => (
                <span key={kind.kind} className="text-[11px] text-ink-faint">
                  <span
                    className={cn(
                      'text-sm font-medium tabular-nums',
                      kind.positive ? 'text-success-text' : 'text-ink-secondary',
                    )}
                  >
                    {stats.byKind[kind.kind]}
                  </span>{' '}
                  {kind.label.toLowerCase()}
                </span>
              ))}
            </div>
          )}

          <div className="space-y-2 mb-4">
            <FilterRow
              label="Status"
              options={ROUTING_FEEDBACK_STATUSES.map((s) => ({ value: s.status, label: s.label, title: s.description }))}
              selected={statuses}
              onToggle={(value) => toggleStatus(value as RoutingFeedbackStatus)}
              onClear={() => setStatuses([])}
            />
            <FilterRow
              label="Kind"
              options={ROUTING_FEEDBACK_KINDS.map((k) => ({ value: k.kind, label: k.label, title: k.description }))}
              selected={kinds}
              onToggle={(value) => toggleKind(value as RoutingFeedbackKind)}
              onClear={() => setKinds([])}
            />
          </div>

          {error && (
            <p className="mb-4 text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-2">
              {error}
            </p>
          )}

          {loading && !queue ? (
            <p className="flex items-center gap-2 text-sm text-ink-faint py-8 justify-center">
              <Loader2 size={14} className="animate-spin" />
              Loading…
            </p>
          ) : (queue?.feedback.length ?? 0) === 0 ? (
            <div className="border border-dashed border-border rounded-lg py-14 text-center">
              <MessageSquare size={22} className="text-border mx-auto mb-3" />
              <p className="text-sm text-ink-secondary mb-1">Nothing here</p>
              <p className="text-xs text-ink-faint max-w-sm mx-auto">
                {statuses.length > 0 || kinds.length > 0
                  ? 'No feedback matches these filters.'
                  : 'Nobody has judged an ingestion run yet. The prompt to do so is on each run in KB Review → Runs.'}
              </p>
            </div>
          ) : (
            <ul className="space-y-2.5">
              {queue?.feedback.map((item) => (
                <FeedbackCard key={item.id} item={item} onResolved={load} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterRow({
  label,
  options,
  selected,
  onToggle,
  onClear,
}: {
  label: string;
  options: Array<{ value: string; label: string; title: string }>;
  selected: string[];
  onToggle: (value: string) => void;
  onClear: () => void;
}) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-faint w-12">{label}</span>
      {options.map((option) => {
        const active = selected.includes(option.value);
        return (
          <button
            key={option.value}
            title={option.title}
            onClick={() => onToggle(option.value)}
            className={cn(
              'px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors border',
              active ? 'bg-ink text-surface border-ink' : 'bg-card text-ink-muted border-border hover:border-orange-300 hover:text-ink',
            )}
          >
            {option.label}
          </button>
        );
      })}
      {/* Selecting none means all, so the filter never has to be reset to see everything. */}
      {selected.length > 0 && (
        <button onClick={onClear} className="px-2 py-1 text-[11px] text-ink-faint hover:text-ink transition-colors">
          Clear
        </button>
      )}
    </div>
  );
}

function FeedbackCard({ item, onResolved }: { item: RoutingFeedbackWithRun; onResolved: () => void }) {
  const sourceLabels = useSourceLabels();
  const kind = routingFeedbackKind(item.kind);
  const status = routingFeedbackStatus(item.status);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<RoutingFeedbackStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resolve = async (next: RoutingFeedbackStatus) => {
    setBusy(next);
    setError(null);
    try {
      await api.admin.resolveRoutingFeedback(item.id, {
        status: next,
        ...(note.trim() ? { resolutionNote: note.trim() } : {}),
      });
      onResolved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that');
    } finally {
      setBusy(null);
    }
  };

  return (
    <li
      className={cn(
        'border rounded-lg bg-card overflow-hidden',
        status.open ? 'border-amber-300 dark:border-amber-900' : 'border-border',
      )}
    >
      <div className="px-4 py-3 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant={kind.positive ? 'success' : 'warning'}>{kind.label}</Badge>
          {item.project && <span className="text-[11px] text-ink-muted">on {item.project.name}</span>}
          {item.segmentIndex !== null && (
            <span className="text-[11px] text-ink-faint">part {item.segmentIndex}</span>
          )}
          {item.suggestedProject && (
            <span className="text-[11px] text-ink-muted">→ should be {item.suggestedProject.name}</span>
          )}
          <Badge variant={status.open ? 'warning' : 'muted'}>{status.label}</Badge>
          <span className="ml-auto text-[11px] text-ink-faint">
            {item.createdBy.name ?? item.createdBy.email} · {timeAgo(item.createdAt)}
          </span>
        </div>

        {item.comment && <p className="text-xs text-ink-secondary leading-relaxed">{item.comment}</p>}

        {/* The run it is about, enough of it to judge the report without leaving the page. */}
        <div className="rounded-md border border-border-subtle bg-surface-subtle px-3 py-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-medium text-ink-secondary truncate max-w-[22rem]">
              {formatRunLabel(item.run.fileName, sourceLabels) || formatSource(item.run.source, sourceLabels)}
            </span>
            {/* Whether the agent chose its own destinations changes how the report should be read: a
                WRONG_PROJECT against a pinned run is a complaint about the person who pinned it. */}
            {item.run.triage && (
              <Badge variant="muted" className="inline-flex items-center gap-1">
                <AlertTriangle size={10} />
                Placed itself
              </Badge>
            )}
            <span className="ml-auto text-[10px] text-ink-faint">{formatDate(item.run.queuedAt)}</span>
          </div>
          {/* Deep-links to the run, which the queue could never do for a decision — the runs list is
              scoped by project and a run knows which project to open it under. */}
          <Link
            to={`/kb-review?tab=runs&run=${item.run.id}`}
            className="text-[11px] text-orange-500 hover:text-orange-600"
          >
            Open this run
          </Link>
        </div>

        {item.resolutionNote && (
          <p className="text-[11px] text-ink-faint leading-snug">
            <span className="text-ink-muted">{status.label}:</span> {item.resolutionNote}
            {item.resolvedBy && <> — {item.resolvedBy.name ?? item.resolvedBy.email}</>}
            {item.resolvedAt && <> · {timeAgo(item.resolvedAt)}</>}
          </p>
        )}

        {error && <p className="text-[11px] text-danger-text">{error}</p>}
      </div>

      <div className="px-4 py-2.5 border-t border-border-subtle bg-surface-subtle flex items-center gap-2 flex-wrap">
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={
            item.status === 'ACTIONED' || item.status === 'DISMISSED'
              ? 'Change the note…'
              : 'What did you change, or why is this not a problem?'
          }
          className="flex-1 min-w-[12rem] text-xs"
        />
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {ROUTING_FEEDBACK_STATUSES.filter((option) => option.status !== item.status).map((option) => (
            <Button
              key={option.status}
              size="sm"
              variant={option.status === 'ACTIONED' ? 'primary' : 'ghost'}
              title={option.description}
              disabled={busy !== null}
              onClick={() => resolve(option.status)}
            >
              {busy === option.status ? (
                <Loader2 size={12} className="mr-1.5 animate-spin" />
              ) : option.status === 'ACTIONED' ? (
                <Check size={12} className="mr-1.5" />
              ) : null}
              {option.label}
            </Button>
          ))}
        </div>
      </div>
    </li>
  );
}
